import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Skeleton,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@org/ui';
import {
  Users,
  FileText,
  MessageCircle,
  Heart,
  TrendingUp,
  BarChart3,
  Activity,
  AlertTriangle,
  Layers,
} from 'lucide-react';
import { format } from 'date-fns';

export const Route = createFileRoute('/admin/analytics')({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { t } = useTranslation('community');

  const { data: stats, isLoading: statsLoading } =
    communityTrpc.analytics.getOverviewStats.useQuery();

  const { data: postsOverTime, isLoading: postsLoading } =
    communityTrpc.analytics.getPostsOverTime.useQuery({ days: 30 });

  const { data: topSpaces, isLoading: spacesLoading } =
    communityTrpc.analytics.getTopSpaces.useQuery({ limit: 10 });

  const { data: engagement, isLoading: engagementLoading } =
    communityTrpc.analytics.getEngagementMetrics.useQuery();

  const statCards = [
    { label: t('admin.analytics.totalUsers'), value: stats?.totalUsers ?? 0, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { label: t('admin.analytics.totalPosts'), value: stats?.totalPosts ?? 0, icon: FileText, color: 'text-green-600', bgColor: 'bg-green-50' },
    { label: t('admin.analytics.totalComments'), value: stats?.totalComments ?? 0, icon: MessageCircle, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { label: t('admin.analytics.totalLikes'), value: stats?.totalLikes ?? 0, icon: Heart, color: 'text-red-600', bgColor: 'bg-red-50' },
    { label: t('admin.analytics.activeToday'), value: stats?.activeUsersToday ?? 0, icon: Activity, color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { label: t('admin.analytics.totalSpaces'), value: stats?.totalSpaces ?? 0, icon: Layers, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
    { label: t('admin.analytics.avgLikesPerPost'), value: engagement?.avgLikesPerPost ?? 0, icon: TrendingUp, color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { label: t('admin.analytics.pendingReports'), value: stats?.pendingReports ?? 0, icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-50', href: '/admin/moderation' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('admin.analytics.title')}</h1>
        <p className="text-muted-foreground">{t('admin.analytics.subtitle')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const content = (
            <Card className={stat.href ? 'transition-shadow hover:shadow-md' : ''}>
              <CardContent className="flex items-center gap-4 p-6">
                <div className={`rounded-full p-3 ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} aria-hidden="true" />
                </div>
                <div>
                  {statsLoading || engagementLoading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    <p className="text-2xl font-bold">{stat.value}</p>
                  )}
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          );

          return stat.href ? (
            <Link key={stat.label} to={stat.href}>
              {content}
            </Link>
          ) : (
            <div key={stat.label}>{content}</div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" aria-hidden="true" />
              {t('admin.analytics.postsOverTime')}
            </CardTitle>
            <CardDescription>{t('admin.analytics.postsOverTimeDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {postsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              <SimpleBarChart data={postsOverTime?.data ?? []} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" aria-hidden="true" />
              {t('admin.analytics.topSpaces')}
            </CardTitle>
            <CardDescription>{t('admin.analytics.topSpacesDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {spacesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : topSpaces?.spaces.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">{t('admin.analytics.noSpaces')}</p>
            ) : (
              <div className="space-y-3">
                {topSpaces?.spaces.map((space, index) => {
                  const maxPosts = topSpaces.spaces[0]?.postCount || 1;
                  const percentage = (space.postCount / maxPosts) * 100;

                  return (
                    <Link
                      key={space.spaceId}
                      to={`/spaces/${space.spaceId}`}
                      className="block"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 text-sm font-medium text-muted-foreground">
                          #{index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{space.name}</span>
                            <span className="text-sm text-muted-foreground">
                              {space.postCount} {t('admin.analytics.posts')}
                            </span>
                          </div>
                          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Posts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" aria-hidden="true" />
            {t('admin.analytics.topEngagingPosts')}
          </CardTitle>
          <CardDescription>{t('admin.analytics.topEngagingPostsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {engagementLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : engagement?.topPosts.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t('admin.analytics.noPosts')}</p>
          ) : (
            <div className="divide-y divide-border">
              {engagement?.topPosts.map((post, index) => (
                <Link
                  key={post.postId}
                  to={`/posts/${post.postId}`}
                  className="flex items-center gap-4 py-4 transition-colors hover:bg-muted/50"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{post.title || t('admin.analytics.untitled')}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Heart className="h-4 w-4" aria-hidden="true" />
                        {post.likesCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="h-4 w-4" aria-hidden="true" />
                        {post.commentsCount}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">{post.engagement}</p>
                    <p className="text-xs text-muted-foreground">{t('admin.analytics.engagement')}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SimpleBarChart({ data }: { data: { date: string; count: number }[] }) {
  const { t } = useTranslation('community');

  if (data.length === 0) {
    return <p className="py-8 text-center text-muted-foreground">{t('admin.analytics.noData')}</p>;
  }

  const maxCount = Math.max(...data.map((d) => d.count), 1);
  const displayData = data.slice(-14);

  return (
    <div className="space-y-4">
      <div className="flex h-40 items-end gap-1">
        {displayData.map((item) => {
          const height = (item.count / maxCount) * 100;
          return (
            <div
              key={item.date}
              className="group relative flex-1"
              title={`${format(new Date(item.date), 'MMM d')}: ${item.count}`}
            >
              <div
                className="w-full rounded-t bg-primary transition-all hover:bg-primary/80"
                style={{ height: `${Math.max(height, 2)}%` }}
              />
              <div className="absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 rounded bg-foreground px-2 py-1 text-xs text-background group-hover:block whitespace-nowrap">
                {format(new Date(item.date), 'MMM d')}: {item.count}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{format(new Date(displayData[0]?.date ?? new Date()), 'MMM d')}</span>
        <span>{format(new Date(displayData[displayData.length - 1]?.date ?? new Date()), 'MMM d')}</span>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <div>
          <p className="text-sm text-muted-foreground">{t('admin.analytics.totalPostsPeriod')}</p>
          <p className="text-2xl font-bold">{displayData.reduce((sum, d) => sum + d.count, 0)}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">{t('admin.analytics.dailyAverage')}</p>
          <p className="text-2xl font-bold">
            {(displayData.reduce((sum, d) => sum + d.count, 0) / displayData.length).toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
}
